/**
 * 发起复议弹窗
 *
 * UI 对齐 web后台.html ReviewModal：
 *  - 操作人信息条
 *  - 复议原因单选（数据正确 / 知识库错误 / 其他）
 *  - 其他原因选中时展示 textarea（必填）
 */
import React, { useEffect, useState } from 'react'
import { Button, Input, Modal } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { CheckListItem } from '../api/check'

const { TextArea } = Input

const REASONS = [
  {
    k: 'data_correct',
    label: '数据正确',
    desc: 'AI 判定有误，水印数据已符合规范'
  },
  {
    k: 'knowledge_wrong',
    label: '知识库错误',
    desc: '规则或参考标准不准确，需更新知识库'
  },
  {
    k: 'other',
    label: '其他原因',
    desc: '需手动录入说明'
  }
]

interface ReviewModalProps {
  visible: boolean
  record: CheckListItem | null
  /** 当前操作用户名，仅用于展示 */
  operatorName?: string
  submitting?: boolean
  onCancel: () => void
  onSubmit: (payload: { reason: string; remark: string }) => void
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  visible,
  record,
  operatorName = '当前用户',
  submitting,
  onCancel,
  onSubmit
}) => {
  const [reason, setReason] = useState<string>('')
  const [other, setOther] = useState('')

  useEffect(() => {
    if (visible) {
      setReason('')
      setOther('')
    }
  }, [visible])

  const valid = reason && (reason !== 'other' || other.trim().length > 0)

  const handleOk = () => {
    if (!valid) return
    const meta = REASONS.find((r) => r.k === reason)
    const reasonLabel = meta?.label || reason
    onSubmit({ reason: reasonLabel, remark: reason === 'other' ? other : '' })
  }

  return (
    <Modal
      title="发起复议"
      open={visible}
      width={520}
      destroyOnHidden
      mask={{ closable: false }}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="ok"
          type="primary"
          disabled={!valid}
          loading={submitting}
          onClick={handleOk}
        >
          确认
        </Button>
      ]}
    >
      {record && (
        <div
          style={{
            background: '#F9FAFB',
            borderRadius: 6,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: '#6B7280',
            marginBottom: 14
          }}
        >
          <UserOutlined style={{ color: '#9CA3AF' }} />
          操作人：
          <span style={{ color: '#1F2937' }}>{operatorName}</span>
          <span style={{ color: '#D9D9D9' }}>|</span>
          <span>{record.inspection}</span>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 2,
            color: '#1F2937'
          }}
        >
          <span style={{ color: '#E84C4C', marginRight: 3 }}>*</span>
          复议原因
        </div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
          请选择复议原因，便于知识库迭代
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {REASONS.map((r) => {
            const active = reason === r.k
            return (
              <div
                key={r.k}
                onClick={() => setReason(r.k)}
                className={
                  'myse-radio-card' + (active ? ' myse-radio-card--active' : '')
                }
              >
                <span
                  className={
                    'myse-radio-card__radio' +
                    (active ? ' myse-radio-card__radio--active' : '')
                  }
                >
                  {active && <span />}
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#1F2937',
                      fontWeight: 500
                    }}
                  >
                    {r.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    {r.desc}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {reason === 'other' && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            <span style={{ color: '#E84C4C', marginRight: 3 }}>*</span>
            请填写复议原因
          </div>
          <TextArea
            value={other}
            onChange={(e) => setOther(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="请填写复议原因……"
          />
        </div>
      )}
    </Modal>
  )
}

export default ReviewModal
